/**
 * The one port a host supplies, and where this package reaches for it.
 *
 * A module-level registry rather than an argument threaded through every store,
 * for the same reason the client is a singleton configured by property setters
 * at boot: a Pinia store is constructed by Pinia, not by us, so there is no call
 * site to pass anything to. `configureLogger` is called once from the host's
 * entry point, beside `configureAdministrationBackend`, and after that a call
 * site just asks.
 *
 * The default WORKS rather than throws. An SDK with nothing configured runs and
 * logs to nowhere, because the alternative is that every call site checks
 * whether the port exists before every call, and one of those checks will be
 * missing.
 *
 * **One port, not five.** The sibling SDK carries device storage, realtime,
 * notifications and geolocation as well. Nothing here persists across a reload
 * and nothing subscribes, so none of those has a requirement forcing it, and a
 * port that cannot name one is not built.
 *
 * **This is a near-copy of the sibling's logger port, and that is deliberate.**
 * Depending on `@hds/auto-auction-com-client-sdk` for a handful of lines would
 * drag in the vibe-server transport, its whole store layer and the event runtime.
 * If a third surface needs the same port it moves down into a shared package
 * then — the first time a second consumer imports it, not on a guess.
 */

// `trace|debug|info|warn|error|fatal(fields, message)` — the `@frankai/logger`
// interface, which is what both halves of the system already speak.
//
// A logger is a port rather than a module in here because where an entry GOES
// is the host's business. A desktop app mirrors to the browser console and
// forwards to Datadog; a test collects into an array. The vocabulary is ours —
// `http.request`, `http.failed` — and stays in this package, on the call sites
// that emit it.
//
// It is also what keeps the logger's NAME honest. `createLogger({ name })`
// stamps every entry with the app that wrote it, and the app is the only thing
// that knows which app it is.
const NO_LOGGING = Object.freeze({
    trace: () => {},
    debug: () => {},
    info: () => {},
    warn: () => {},
    error: () => {},
    fatal: () => {}
});

let logger = NO_LOGGING;

/**
 * Install the host's logger.
 *
 * @param {Object} supplied
 * @param {Object} [supplied.logger] { trace|debug|info|warn|error|fatal(fields, message) }
 */
export function configureLogger({ logger: supplied } = {}) {
    if (supplied !== undefined) {
        logger = supplied;
    }
    return logger;
}

export const useLogger = () => logger;

/**
 * Put the port back to its default. For tests — a port left installed by one
 * case is the kind of leak that makes the next one pass for the wrong reason.
 */
export function resetPorts() {
    logger = NO_LOGGING;
    return logger;
}
